import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { usePlaidLink } from 'react-plaid-link';
import { plaidApi } from '../services/api';
import PrivacyPolicyModal from './UI/PrivacyPolicyModal';
import LoadingSpinner from './UI/LoadingSpinner';

interface BankAccount {
  id: string;
  account_id: string;
  account_number_last4: string;
  routing_number?: string;
  bank_name: string;
  account_type: 'checking' | 'savings' | string;
  status: 'active' | 'inactive' | 'pending';
  created_at: string;
}

interface BankLinkingProps {
  onSuccess?: () => void;
}

const BankLinking: React.FC<BankLinkingProps> = ({ onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [hasAcceptedPrivacy, setHasAcceptedPrivacy] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [plaidLinkToken, setPlaidLinkToken] = useState<string | null>(null);

  useEffect(() => {
    loadBankAccounts();
  }, []);

  const loadBankAccounts = async () => {
    try {
      setIsLoading(true);
      // Load accounts from Plaid only
      const response = await plaidApi.getAccounts();
      
      if (response.data?.success && response.data.data?.accounts) {
        const plaidAccounts = response.data.data.accounts.map((acc: any) => ({
          id: acc.id,
          account_id: acc.plaid_account_id,
          account_number_last4: acc.last4,
          bank_name: acc.institution_name,
          account_type: acc.subtype || acc.account_type,
          status: acc.status,
          created_at: acc.created_at,
        }));
        setAccounts(plaidAccounts);
      }
    } catch (error) {
      console.error('Failed to load bank accounts:', error);
      toast.error('Failed to load bank accounts');
    } finally {
      setIsLoading(false);
    }
  };

  // Plaid Link configuration
  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
    token: plaidLinkToken,
    onSuccess: async (publicToken, metadata) => {
      try {
        setIsLinking(true);
        const response = await plaidApi.exchangeToken({
          public_token: publicToken,
          accounts: metadata.accounts,
          institution: metadata.institution,
        });
        
        if (response.data?.success && response.data?.data) {
          toast.success(`Successfully linked ${response.data.data.linked} account(s) via Plaid!`);
          loadBankAccounts();
          onSuccess?.();
        } else {
          toast.error('Failed to complete Plaid connection');
        }
      } catch (error) {
        console.error('Failed to exchange Plaid token:', error);
        toast.error('Failed to link bank account via Plaid');
      } finally {
        setIsLinking(false);
        setPlaidLinkToken(null);
      }
    },
    onExit: (err) => {
      if (err) {
        console.error('Plaid Link error:', err);
      }
      setIsLinking(false);
      setPlaidLinkToken(null);
    },
  });

  // Open Plaid Link when token is ready
  useEffect(() => {
    if (plaidLinkToken && plaidReady) {
      openPlaidLink();
    }
  }, [plaidLinkToken, plaidReady, openPlaidLink]);

  const handleLinkWithPlaid = async () => {
    if (!hasAcceptedPrivacy) {
      setShowPrivacyPolicy(true);
      return;
    }

    try {
      setIsLinking(true);
      const response = await plaidApi.createLinkToken();
      
      if (response.data?.success && response.data.data?.link_token) {
        setPlaidLinkToken(response.data.data.link_token);
      } else {
        toast.error('Failed to initialize Plaid');
      }
    } catch (error) {
      console.error('Failed to create Plaid link token:', error);
      toast.error('Failed to initialize Plaid connection');
      setIsLinking(false);
    }
  };

  const handlePrivacyAccept = () => {
    setHasAcceptedPrivacy(true);
    setShowPrivacyPolicy(false);
    // Proceed with Plaid linking
    handleLinkWithPlaid();
  };

  const formatAccountType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-400';
      case 'pending':
        return 'text-yellow-400';
      case 'inactive':
        return 'text-red-400';
      default:
        return 'text-neutral-400';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-xl font-semibold text-neutral-100 mb-2">
          Link Your Bank Account
        </h3>
        <p className="text-neutral-400">
          Connect your bank account to fund your investments securely via ACH transfers
        </p>
      </div>

      {/* Bank Link Option */}
      <div className="max-w-md mx-auto">
        {/* Plaid Option - Primary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-6 relative border-2 border-honey-500"
        >
          <div className="absolute -top-3 left-4 bg-honey-500 text-neutral-900 text-xs font-bold px-2 py-1 rounded">
            RECOMMENDED
          </div>
          <div className="mb-4 text-center">
            <div className="p-4 bg-honey-500 bg-opacity-20 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-honey-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-neutral-100 mb-2">
              Connect with Plaid
            </h4>
            <p className="text-neutral-400 text-sm mb-4">
              Link your bank instantly with Plaid's secure connection. Supports 12,000+ banks.
            </p>
          </div>

          <button
            onClick={handleLinkWithPlaid}
            disabled={isLinking}
            className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLinking ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Connecting...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <span>Link with Plaid</span>
              </>
            )}
          </button>
        </motion.div>
      </div>

      {!hasAcceptedPrivacy && (
        <p className="text-xs text-neutral-500 text-center">
          By linking your bank account, you agree to our{' '}
          <button
            onClick={() => setShowPrivacyPolicy(true)}
            className="text-honey-500 hover:text-honey-400 underline"
          >
            Privacy Policy
          </button>
        </p>
      )}

      {/* Linked Accounts */}
      {accounts.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-6"
        >
          <h4 className="text-lg font-semibold text-neutral-100 mb-4">
            Linked Bank Accounts
          </h4>

          <div className="space-y-4">
            {accounts.map((account) => (
              <div
                key={account.id}
                className="flex items-center justify-between p-4 bg-neutral-700 rounded-lg"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-3 rounded-full bg-honey-500 bg-opacity-20">
                    <svg className="w-6 h-6 text-honey-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-100">
                      {account.bank_name} ••••{account.account_number_last4}
                    </p>
                    <p className="text-sm text-neutral-400">
                      {formatAccountType(account.account_type)} {account.routing_number && `• Routing: ${account.routing_number}`}
                    </p>
                    {account.created_at && (
                      <span className="text-xs text-neutral-500">
                        Linked {new Date(account.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-right">
                  <span className={`text-sm font-medium ${getStatusColor(account.status)}`}>
                    {account.status.charAt(0).toUpperCase() + account.status.slice(1)}
                  </span>
                  {account.status === 'active' && (
                    <div className="mt-2">
                      <button className="text-xs text-honey-500 hover:text-honey-400 underline">
                        Set as Default
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Security Notice */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-neutral-800 border border-neutral-700 rounded-lg p-4"
      >
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-green-500 bg-opacity-20 rounded-full flex-shrink-0">
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <h5 className="text-sm font-semibold text-neutral-100 mb-1">
              Bank-Level Security
            </h5>
            <p className="text-xs text-neutral-400">
              Your bank credentials are never stored on our servers. We use industry-leading 
              providers (Plaid and Stripe) for secure, tokenized access to your account information. 
              All data is encrypted with AES-256 and transmitted over secure TLS connections.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isOpen={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy(false)}
        onAccept={handlePrivacyAccept}
      />
    </div>
  );
};

export default BankLinking;